import {
  ArrowDownward,
  AttachFileOutlined,
  CloseRounded,
} from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import FileUploadField from "../inputs/FileUploadField";
import InputWrapper from "../inputs/shared/InputWrapper";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import DocTypeCategoryPicker from "../inputs/DocTypeCategoryPicker";
import { grey, yellow } from "@mui/material/colors";

const FileUploadProcess = ({
  initialFiles,
  docType,
  docGroup,
  keyName,
  sx,
  loading,
  background,
  onSubmit,
  uploadText,
  uploadTextStartContent,
  uploadTextEndContent,
  uploadTextTopContent,
  uploadTextBottomContent,
  borderSize = 1.5,
  categoryAndTypeLabel,
  uploadBoxSx,
  disableCategoryAndTypeSelection = false,
  hideCategoryAndTypeSelection = false,
  showDivider = false,
  onCompleteFn,
  uploadHeight,
  disableCategoryAndTypeSelectionCount = false,
  allowedDocGroups,
  // When true, the staged-files list switches from a single "type for all"
  // picker to per-selection assignment: each row gets a checkbox, picking
  // rows reveals the doc-type picker, choosing a type applies it to just the
  // selected rows (then clears the selection), and the assigned type shows
  // as its own column. Opt-in so existing single-type uploads are unchanged.
  perFileDocType = false,
  children,
}) => {
  const [files, setFiles] = useState(initialFiles);
  const [assignedDocGroup, setAssignedDocGroup] = useState(docGroup || null);
  const [assignedDocType, setAssignedDocType] = useState(docType || null);
  // Per-file mode: ids of staged rows currently checked. The grid api is held
  // so we can clear the checkboxes after applying a type.
  const [selectedIds, setSelectedIds] = useState([]);
  const gridApiRef = useRef(null);

  const applyDocTypeToSelected = useCallback(
    (dg, dt) => {
      if (!dg && !dt) return; // ignore the picker's "cleared" event
      if (selectedIds.length === 0) return;
      setFiles((prev) =>
        (prev || []).map((f) =>
          selectedIds.includes(f.id) ? { ...f, docGroup: dg, docType: dt } : f,
        ),
      );
      if (gridApiRef.current) gridApiRef.current.deselectAll();
      setSelectedIds([]);
    },
    [selectedIds],
  );
  const docGroupTypeLabel = useMemo(() => {
    if (categoryAndTypeLabel) {
      return categoryAndTypeLabel;
    }

    return "Select Document Type";
    // return "Select Document Category";
  }, [categoryAndTypeLabel]);

  const updateFilesMetadata = (files, docGroup, docType) => {
    // Guard clause for empty files array
    if (!files || files.length === 0) {
      return [];
    }

    // Create a new array with updated metadata
    return files.map((file) => {
      // Create a copy of the file to avoid mutating the original
      const updatedFile = { ...file };

      // Update docGroup if provided
      if (docGroup !== undefined && docGroup !== null) {
        updatedFile.docGroup = docGroup;

        // Clear docType if docGroup changes and the types don't match
        // This assumes docType is dependent on docGroup
        if (updatedFile.docType && docType === undefined) {
          updatedFile.docType = undefined;
        }
      }

      // Update docType if provided
      if (docType !== undefined && docType !== null) {
        updatedFile.docType = docType;
      }

      // Clear both if both are nil
      if (isNil(docType) && isNil(docGroup)) {
        updatedFile.docGroup = undefined;
        updatedFile.docType = undefined;
      }

      return updatedFile;
    });
  };

  const onDocGroupTypeChange = useCallback(
    (dg, dt) => {
      // console.log({ dg, dt });
      let updatedFiles = updateFilesMetadata(files, dg, dt);
      setAssignedDocGroup(dg);
      setAssignedDocType(dt);
      setFiles(updatedFiles);
    },
    [files],
  );

  const onFilesAdded = useCallback(
    (newFiles) => {
      // console.log("newFiles", newFiles);
      // Use the assigned values from state, not the callback params
      let updatedFiles = updateFilesMetadata(
        newFiles,
        assignedDocGroup,
        assignedDocType,
      );
      setFiles((prevFiles) => {
        if (isEmpty(prevFiles)) {
          return updatedFiles;
        }
        return [...prevFiles, ...updatedFiles];
      });
    },
    [assignedDocGroup, assignedDocType],
  );
  const handleUploadFiles = useCallback(
    (f) => {
      onSubmit(f, {
        onSuccessFn: (sd) => {
          setFiles([]);
          if (onCompleteFn) {
            onCompleteFn(sd);
          }
        },
      });
    },
    [onSubmit, onCompleteFn],
  );
  // Apply initial docGroup/docType to initial files
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && (docGroup || docType)) {
      const updatedFiles = updateFilesMetadata(initialFiles, docGroup, docType);
      setFiles(updatedFiles);
    }
  }, [initialFiles, docGroup, docType]);

  // Per-file mode: once the staged list empties (Clear All / after upload),
  // drop any lingering selection so the picker doesn't show a stale count
  // when the next batch of files is added.
  useEffect(() => {
    if (perFileDocType && isEmpty(files) && selectedIds.length > 0) {
      setSelectedIds([]);
    }
  }, [perFileDocType, files, selectedIds.length]);

  return (
    <Flx column sx={sx}>
      <FileUploadField
        key={`${keyName}-initial-upload`}
        id={`${keyName}-initial-file-upload`}
        suppressUploadButton
        borderSize={borderSize}
        height={uploadHeight}
        minHeight={uploadHeight}
        uploadText={uploadText}
        uploadTextStartContent={uploadTextStartContent}
        uploadTextEndContent={uploadTextEndContent}
        uploadTextTopContent={uploadTextTopContent}
        uploadTextBottomContent={uploadTextBottomContent}
        background={background}
        docGroup={docGroup}
        docType={docType}
        hideFileList
        sx={uploadBoxSx}
        onFilesAdded={onFilesAdded}
      />
      <UploadedFilesTable
        files={files}
        setFiles={setFiles}
        perFileDocType={perFileDocType}
        onSelectionChanged={setSelectedIds}
        onGridReady={(api) => (gridApiRef.current = api)}
      />
      {isEmpty(files) ? null : (
        <>
          <Flx fw column center sx={{ mt: 1 }}>
            {/* <ArrowDownward sx={{ color: "#33475b" }} /> */}
            <Flx
              fw
              column
              g={2}
              sx={{
                // mt: 1,
                // background: "#fffde7b6",
                ...(hideCategoryAndTypeSelection
                  ? {}
                  : {
                      borderWidth: isEmpty(selectedIds) ? 0 : 3,
                      borderStyle: "solid",
                      borderColor: "#e4f2fd",
                      // borderColor: grey[200],
                      borderRadius: 2,
                      p: 2,
                    }),
              }}
            >
              {!hideCategoryAndTypeSelection &&
                (perFileDocType ? (
                  selectedIds.length > 0 ? (
                    <InputWrapper
                      labelSx={{ fontWeight: 700 }}
                      label={`Set document type for (${selectedIds.length}) selected file${
                        selectedIds.length === 1 ? "" : "s"
                      }`}
                    >
                      <DocTypeCategoryPicker
                        onChange={(params) =>
                          applyDocTypeToSelected(
                            params?.docGroup,
                            params?.docType,
                          )
                        }
                        disabled={disableCategoryAndTypeSelection}
                        docGroup={null}
                        docType={null}
                        allowedDocGroups={allowedDocGroups}
                      />
                    </InputWrapper>
                  ) : null
                ) : (
                  // <Txt secondary sx={{ fontSize: 13 }}>
                  //   Check one or more files above to assign their document
                  //   type.
                  // </Txt>
                  <InputWrapper
                    labelSx={{
                      fontWeight: 700,
                      // fontStyle: "italic",
                      //
                    }}
                    label={
                      disableCategoryAndTypeSelectionCount
                        ? docGroupTypeLabel
                        : `${docGroupTypeLabel} (${size(files)})`
                    }
                  >
                    <DocTypeCategoryPicker
                      onChange={(params) =>
                        onDocGroupTypeChange(params?.docGroup, params?.docType)
                      }
                      disabled={disableCategoryAndTypeSelection}
                      docGroup={assignedDocGroup}
                      docType={assignedDocType}
                      allowedDocGroups={allowedDocGroups}
                    />
                  </InputWrapper>
                ))}
              <Flx
                g={1}
                fw
                ac
                end
                wrap
                sx={{
                  mt: isEmpty(selectedIds) ? 1 : 0,
                }}
              >
                <Button
                  onClick={() => setFiles(null)}
                  variant="outlined"
                  size="large"
                  color="grey"
                >
                  Clear All
                </Button>

                <Button
                  loading={loading}
                  onClick={() => handleUploadFiles(files)}
                  size="large"
                  color="primary"
                >
                  Upload Files ({size(files)})
                </Button>
              </Flx>
            </Flx>
          </Flx>
          {showDivider && <Divider sx={{ my: 4, borderColor: grey[200] }} />}
        </>
      )}
      {children}
    </Flx>
  );
};

const UploadedFilesTable = ({
  files,
  setFiles,
  perFileDocType = false,
  onSelectionChanged,
  onGridReady,
}) => {
  const ref = useRef();

  const modalContentRef = useRef(null);

  const columnDefs = useMemo(() => {
    // Leading column: a checkbox (per-file mode) or the static attach icon.
    const leadColumn = perFileDocType
      ? {
          headerName: "",
          checkboxSelection: true,
          headerCheckboxSelection: true,
          resizable: false,
          maxWidth: 44,
          width: 44,
          // cellStyle: {
          //   paddingLeft: 0,
          //   paddingRight: 0,
          //   display: "flex",
          //   alignItems: "center",
          //   justifyContent: "center",
          // },
        }
      : {
          resizable: false,
          cellRenderer: () => (
            <AttachFileOutlined className="thin" sx={{ fontSize: 16 }} />
          ),
          maxWidth: 32,
          cellStyle: {
            // paddingLeft: 0,
            // paddingRight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        };

    const cols = [
      leadColumn,
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 150,
        cellEditor: "agTextCellEditor",
        editable: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          cursor: "text",
          gap: "6px",
          paddingLeft: "0px",
          // paddingLeft: "8px",
        },
      },
    ];

    // Per-file mode surfaces the assigned doc type as its own column so the
    // result of each assignment is visible in the staged list.
    if (perFileDocType) {
      cols.push({
        headerName: "Type",
        field: "docType",
        flex: 1,
        minWidth: 130,
        editable: false,
        valueFormatter: (params) => params.value || "Unassigned",
        cellStyle: (params) => ({
          display: "flex",
          alignItems: "center",
          fontStyle: params.value ? "normal" : "italic",
          color: params.value ? "#334155" : "#94a3b8",
        }),
      });
    }

    cols.push({
      width: 38,
      headerName: "",
      pinned: "right",
      cellStyle: {
        paddingRight: 0,
        paddingLeft: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      },
      field: "delete",
      cellRenderer: () => <CloseRounded className="thin" color="error" />,
    });

    return cols;
  }, [perFileDocType]);

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      // Create a copy of the data to update
      const updatedData = { ...data };

      // If the user changes the filename
      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        updatedData.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
      }

      // If the user changes docGroup
      if (colDef.field === "docGroup") {
        updatedData.docGroup = newValue;
        // Clear docType when docGroup changes
        updatedData.docType = "";
      }

      // If the user changes docType
      if (colDef.field === "docType") {
        updatedData.docType = newValue;
      }

      // Update the parent files state
      setFiles((prev) =>
        prev.map((item) => (item.id === data.id ? updatedData : item)),
      );

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  const onCellClicked = useCallback(
    (params) => {
      const { colDef, data } = params;

      if (colDef.field === "delete") {
        setFiles((prev) => prev.filter((item) => item.id !== data.id));
      }
    },
    [setFiles],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      //   ...columnTypesNumber(),
    };
  }, []);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid #eeeeee",
        borderTop: "none",
        overflow: "hidden",
        borderBottomRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        // mt: 1,
        ".ag-header": {
          border: "none !important",
        },
        ".ag-pinned-right-cols-container .ag-cell": {
          borderLeft: "1px solid #eeeeee !important",
        },

        ".ag-row-last": {
          borderBottom: "none !important",
        },
      }}
    >
      <AgGridTableWrapper simpleTable suppressMinHeightWhenPopulated={files}>
        <AgGridReact
          ref={ref}
          rowData={files}
          columnDefs={columnDefs}
          singleClickEdit
          onCellClicked={onCellClicked}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          // Per-file mode shows the column header (for the select-all
          // checkbox + "Type" label); single-type mode keeps it hidden.
          headerHeight={perFileDocType ? 36 : 0}
          onCellValueChanged={handleCellValueChanged}
          stopEditingWhenCellsLoseFocus
          popupParent={modalContentRef.current}
          {...(perFileDocType
            ? {
                rowSelection: "multiple",
                suppressRowClickSelection: true,
                getRowId: (params) => params.data.id,
                onGridReady: (e) => onGridReady?.(e.api),
                onSelectionChanged: (e) =>
                  onSelectionChanged?.(
                    e.api.getSelectedRows().map((r) => r.id),
                  ),
              }
            : {})}
        />
      </AgGridTableWrapper>
    </Box>
  );
};
export default FileUploadProcess;
